import React, {
  memo,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useSelector } from 'react-redux';
import { RootState, useAppDispatch } from '../../support/store';
import { setFields, setQueryResult } from '../../support/slices/tileBuilderSlice';
import {
  flattenFields,
  getPipelineStepOutputs,
  getPreviewTileDataAndDefinition,
} from '../../support/customTileTransformUtils';
import LineChartTile from '../Dashboard/Tiles/LineChartTile';
import PieChartTile from '../Dashboard/Tiles/PieChartTile';
import MetricChartTile from '../Dashboard/Tiles/MetricChartTile';
import DataGridTile from '../Dashboard/Tiles/DataGridTile';
import DonutChartTile from '../Dashboard/Tiles/DonutChartTile';
import BarChartTile from '../Dashboard/Tiles/BarChartTile';
import GaugeChartTile from '../Dashboard/Tiles/GaugeChartTile';
import { Query, TileSettings, Transformation } from '../../types/tileBuilderTypes';
import { TileDefinition } from '../../types/dashboard';
import DataLoading from './DataLoading';
import { TileRuntimeContext } from '../../support/tileRuntimeContext';
import { fetchCustomTileQueryResults } from '../../support/customTileDataFetcher';

function normalizeRuntimeType(type?: string): string {
  const t = (type || '').toLowerCase();
  if (t.includes('bar')) return 'bar_chart';
  if (t.includes('line')) return 'line_chart';
  if (t.includes('pie')) return 'pie_chart';
  if (t.includes('donut')) return 'donut_chart';
  if (t.includes('gauge')) return 'gauge_chart';
  if (t.includes('datagrid')) return 'datagrid';
  if (t.includes('metric')) return 'metric_chart';
  return type || '';
}

function inferBarDirection(type?: string, settings?: any): 'vertical' | 'horizontal' {
  const t = (type || '').toLowerCase();
  if (
    settings?.layout === 'horizontal' ||
    settings?.orientation === 'horizontal' ||
    t.includes('horizontal')
  ) {
    return 'horizontal';
  }
  return 'vertical';
}

function deriveSeriesKeysFromData(data: any[]): string[] {
  const first = Array.isArray(data) && data.length > 0 ? data[0] : null;
  if (!first) return [];
  if (Array.isArray((first as any).values)) {
    return (first as any).values.map((v: any) => String(v.key));
  }
  return Object.keys(first).filter(
    (k) => k !== 'label' && k !== '__tooltip' && typeof (first as any)[k] === 'number'
  );
}

const stableStringify = (value: any): string => {
  const sortKeys = (obj: any): any => {
    if (Array.isArray(obj)) return obj.map(sortKeys);
    if (obj && typeof obj === 'object') {
      return Object.keys(obj)
        .sort()
        .reduce((acc: any, key) => {
          acc[key] = sortKeys(obj[key]);
          return acc;
        }, {});
    }
    return obj;
  };

  try {
    return JSON.stringify(sortKeys(value));
  } catch {
    return '';
  }
};

function stripQueryResultsForSignature(queries: Query[] = []) {
  return queries.map((q: any) => {
    const { queryResult, functionResult, ...rest } = q || {};
    return rest;
  });
}

interface DataVisualizationComponentProps {
  tileDefinition: TileDefinition;
  runtimeContext?: any;
  settingsOverride?: Record<string, any>;
  currentTimeFilter?: string;
  onCurrentTimeFilterChange?: (value: string) => void;
  setTileError?: (error: string | null) => void;
  suppressReduxInit?: any;
  customData?: any;
  queriesOverride?: any[];
  transformationsOverride?: any[];
  refetchTrigger?: any;
  displayContext?: 'preview' | 'dashboard' | 'leader';
}

const MIN_RENDER_WIDTH = 80;
const SIZE_EPSILON_PX = 2;

const DataVisualizationComponent: React.FC<DataVisualizationComponentProps> = memo(
  ({
     tileDefinition,
     customData,
     queriesOverride,
     transformationsOverride,
     settingsOverride,
     suppressReduxInit,
     refetchTrigger,
     setTileError,
     runtimeContext = {},
     currentTimeFilter,
     displayContext = 'dashboard',
   }) => {
    const chartId = `tile-${tileDefinition.id}`;
    const dispatch = useAppDispatch();

    const containerRef = useRef<HTMLDivElement>(null);
    const [layoutHeight, setLayoutHeight] = useState(300);
    const [layoutWidth, setLayoutWidth] = useState(0);
    const [layoutVersion, setLayoutVersion] = useState(0);
    const lastSizeRef = useRef<{ w: number; h: number }>({ w: 0, h: 0 });

    const [initialized, setInitialized] = useState(false);
    const [isFetching, setIsFetching] = useState(false);
    const [pipelineError, setPipelineError] = useState<string | null>(null);
    const [fetchedQueryResults, setFetchedQueryResults] = useState<any[]>([]);

    const userData: any = useSelector((state: RootState) => state.user?.userData);

    const tileBuilderEntry = useSelector(
      (state: RootState) => (state.tileBuilder as any)?.[chartId]
    );

    const reduxPreviewRuntimeContext: TileRuntimeContext = useMemo(
      () => tileBuilderEntry?.preview?.runtimeContext || {},
      [tileBuilderEntry?.preview?.runtimeContext]
    );

    const hasTileBuilderEntry = !!tileBuilderEntry;
    const hasRuntimeContextProp = !!runtimeContext && Object.keys(runtimeContext).length > 0;

    const effectiveCurrentTimeFilter = useMemo(() => {
      return (
        currentTimeFilter ??
        (hasRuntimeContextProp
          ? runtimeContext?.current_time_filter
          : hasTileBuilderEntry
            ? reduxPreviewRuntimeContext?.current_time_filter
            : undefined) ??
        tileDefinition?.settings?.defaultDateRange ??
        'all'
      );
    }, [
      currentTimeFilter,
      hasRuntimeContextProp,
      runtimeContext?.current_time_filter,
      hasTileBuilderEntry,
      reduxPreviewRuntimeContext?.current_time_filter,
      tileDefinition?.settings?.defaultDateRange,
    ]);

    const effectiveRuntimeContext: TileRuntimeContext = useMemo(() => {
      if (hasRuntimeContextProp) {
        return {
          ...(runtimeContext ?? {}),
          current_time_filter: effectiveCurrentTimeFilter,
        };
      }

      if (hasTileBuilderEntry) {
        return {
          ...(reduxPreviewRuntimeContext ?? {}),
          current_time_filter: effectiveCurrentTimeFilter,
        };
      }

      return {
        current_time_filter: effectiveCurrentTimeFilter,
      };
    }, [
      hasRuntimeContextProp,
      runtimeContext,
      hasTileBuilderEntry,
      reduxPreviewRuntimeContext,
      effectiveCurrentTimeFilter,
    ]);

    const availableVariables = useMemo(
      () => ({
        name: userData?.name ?? '',
        email:
          effectiveRuntimeContext?.email ??
          effectiveRuntimeContext?.userEmail ??
          effectiveRuntimeContext?.ownerEmail ??
          userData?.email ??
          '',
        sfId: effectiveRuntimeContext?.sfId ?? userData?.sfId ?? '',
        managerEmail: effectiveRuntimeContext?.managerEmail ?? '',
        domain: (effectiveRuntimeContext?.domain as string) ?? '',
        current_time_filter:
          (effectiveRuntimeContext?.current_time_filter as string) ??
          tileDefinition?.settings?.defaultDateRange ??
          'all',
      }),
      [
        userData?.name,
        userData?.email,
        userData?.sfId,
        effectiveRuntimeContext?.email,
        effectiveRuntimeContext?.userEmail,
        effectiveRuntimeContext?.ownerEmail,
        effectiveRuntimeContext?.sfId,
        effectiveRuntimeContext?.managerEmail,
        effectiveRuntimeContext?.domain,
        effectiveRuntimeContext?.current_time_filter,
        tileDefinition?.settings?.defaultDateRange,
      ]
    );

    const effectiveQueriesForFetch = useMemo<Query[]>(() => {
      return (queriesOverride ?? tileDefinition.queries ?? []) as Query[];
    }, [queriesOverride, tileDefinition.queries]);

    const effectiveTransformations = useMemo<Transformation[]>(() => {
      return (transformationsOverride ?? tileDefinition.transformations ?? []) as Transformation[];
    }, [transformationsOverride, tileDefinition.transformations]);

    const effectiveSettings = useMemo<TileSettings>(() => {
      return {
        ...(tileDefinition.settings ?? {}),
        ...(settingsOverride ?? {}),
      };
    }, [tileDefinition.settings, settingsOverride]);

    const runtimeContextSignature = useMemo(
      () => stableStringify(effectiveRuntimeContext),
      [effectiveRuntimeContext]
    );

    const availableVariablesSignature = useMemo(
      () => stableStringify(availableVariables),
      [availableVariables]
    );

    const queriesFetchSignature = useMemo(
      () => stableStringify(stripQueryResultsForSignature(effectiveQueriesForFetch)),
      [effectiveQueriesForFetch]
    );

    const transformationsSignature = useMemo(
      () => stableStringify(effectiveTransformations),
      [effectiveTransformations]
    );

    const fetchSignature = useMemo(
      () =>
        stableStringify({
          chartId,
          suppressReduxInit: !!suppressReduxInit,
          refetchTrigger: refetchTrigger ?? null,
          runtimeContextSignature,
          availableVariablesSignature,
          queriesFetchSignature,
          transformationsSignature,
        }),
      [
        chartId,
        suppressReduxInit,
        refetchTrigger,
        runtimeContextSignature,
        availableVariablesSignature,
        queriesFetchSignature,
        transformationsSignature,
      ]
    );

    const lastFetchSignatureRef = useRef<string>('');
    const lastFieldsSignatureRef = useRef<string>('');
    const hasCompletedInitialFetchRef = useRef(false);

    useLayoutEffect(() => {
      if (!containerRef.current) return;

      const rect = containerRef.current.getBoundingClientRect();
      const w = rect.width > 0 ? Math.floor(rect.width) : 0;
      const h = rect.height > 0 ? Math.floor(rect.height) : 0;

      const prev = lastSizeRef.current;
      // Commit size state (and bump layoutVersion) ONLY on a real ≥epsilon change. layoutWidth/
      // layoutHeight feed chartRenderKey and the `${w}x${h}` remount keys, so writing them on every
      // sub-pixel ResizeObserver/layout tick remounts the responsive donut/pie/gauge (100%×100%,
      // no definite-height ancestor) continuously — the "blinking": each redraw nudges the container
      // ~1px, which re-writes the size state, which remounts, which redraws… A remounting chart also
      // momentarily measures 0, which must never be committed. Gating BOTH the state writes and the
      // version bump behind the same epsilon keeps the real-resize remount (the implicit height clamp)
      // while killing the jitter loop.
      if (
        w > 0 &&
        h > 0 &&
        (Math.abs(w - prev.w) >= SIZE_EPSILON_PX || Math.abs(h - prev.h) >= SIZE_EPSILON_PX)
      ) {
        lastSizeRef.current = { w, h };
        setLayoutWidth(w);
        setLayoutHeight(h);
        setLayoutVersion((v) => v + 1);
      }
    }, [tileDefinition.id]);

    useEffect(() => {
      if (!containerRef.current || typeof ResizeObserver === 'undefined') return;

      const ro = new ResizeObserver((entries) => {
        for (const entry of entries) {
          const { width, height } = entry.contentRect;
          const w = width > 0 ? Math.floor(width) : 0;
          const h = height > 0 ? Math.floor(height) : 0;

          const prev = lastSizeRef.current;
          // Same epsilon gate as the useLayoutEffect above: commit layoutWidth/layoutHeight (which
          // feed chartRenderKey + the `${w}x${h}` remount keys) and bump the version ONLY on a real
          // ≥epsilon resize. Writing on every sub-pixel tick — or on a 0-sized remount transient —
          // is what makes the responsive charts blink. Real resizes still remount/clamp.
          if (
            w > 0 &&
            h > 0 &&
            (Math.abs(w - prev.w) >= SIZE_EPSILON_PX || Math.abs(h - prev.h) >= SIZE_EPSILON_PX)
          ) {
            lastSizeRef.current = { w, h };
            setLayoutWidth(w);
            setLayoutHeight(h);
            setLayoutVersion((v) => v + 1);
          }
        }
      });

      ro.observe(containerRef.current);
      return () => ro.disconnect();
    }, [tileDefinition.id]);

    const lastClearedFetchSignatureRef = useRef<string>('');

    useEffect(() => {
      if (!setTileError) return;
      if (lastClearedFetchSignatureRef.current === fetchSignature) return;

      lastClearedFetchSignatureRef.current = fetchSignature;
      setTileError(null);
    }, [fetchSignature, setTileError]);

    useEffect(() => {
      let cancelled = false;

      const shouldFetch = !suppressReduxInit && (effectiveQueriesForFetch?.length ?? 0) > 0;

      if (!shouldFetch) {
        setPipelineError(null);
        setIsFetching(false);
        if (!hasCompletedInitialFetchRef.current) {
          hasCompletedInitialFetchRef.current = true;
          setInitialized(true);
        }
        return () => {
          cancelled = true;
        };
      }

      if (lastFetchSignatureRef.current === fetchSignature) {
        return () => {
          cancelled = true;
        };
      }

      lastFetchSignatureRef.current = fetchSignature;
      setPipelineError(null);
      setIsFetching(true);

      fetchCustomTileQueryResults({
        queries: effectiveQueriesForFetch,
        availableVariables,
        runtimeContext: effectiveRuntimeContext,
      })
        .then((results) => {
          if (cancelled) return;

          const safeResults = Array.isArray(results) ? results : [];
          setFetchedQueryResults(safeResults);

          if (hasTileBuilderEntry) {
            safeResults.forEach((result, index) => {
              dispatch(setQueryResult({ chartId, index, result }));
            });
          }

          setIsFetching(false);
          if (!hasCompletedInitialFetchRef.current) {
            hasCompletedInitialFetchRef.current = true;
          }
          setInitialized(true);
        })
        .catch((err) => {
          if (cancelled) return;
          setPipelineError(`Error fetching data: ${err?.message || err}`);
          setIsFetching(false);
          if (!hasCompletedInitialFetchRef.current) {
            hasCompletedInitialFetchRef.current = true;
          }
          setInitialized(true);
        });

      return () => {
        cancelled = true;
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [fetchSignature, hasTileBuilderEntry, dispatch, chartId]);

    const processed = useMemo(() => {
      try {
        let runtimeQueries: Query[];
        let runtimeTransformations: Transformation[];
        let runtimeSettings: TileSettings;
        let transformedData: any;

        if (suppressReduxInit) {
          runtimeQueries = (queriesOverride || []) as Query[];
          runtimeTransformations = (transformationsOverride || []) as Transformation[];
          runtimeSettings = (settingsOverride ?? tileDefinition.settings ?? {}) as TileSettings;
        } else {
          runtimeQueries = (effectiveQueriesForFetch || []).map((q, i) => ({
            ...q,
            queryResult: fetchedQueryResults[i] || [],
          })) as Query[];

          runtimeTransformations = effectiveTransformations;
          runtimeSettings = effectiveSettings;
        }

        const queriesWithResults = runtimeQueries.map((q: any) => ({
          ...q,
          queryResult: q.functionResult ?? q.queryResult ?? [],
        }));

        const pipelineOutputs = getPipelineStepOutputs(queriesWithResults, runtimeTransformations);

        transformedData =
          suppressReduxInit && customData
            ? customData
            : pipelineOutputs?.length
              ? pipelineOutputs[pipelineOutputs.length - 1]
              : [];

        if (
          !Array.isArray(transformedData) ||
          (transformedData.length > 0 && typeof transformedData[0] !== 'object')
        ) {
          transformedData = Array.isArray(transformedData)
            ? transformedData.map((v) => ({ value: v }))
            : [];
        }

        const isLineChart = normalizeRuntimeType(tileDefinition.chart_type) === 'line_chart';
        const looksLikeLineSeries =
          Array.isArray(transformedData) &&
          transformedData.length > 0 &&
          typeof transformedData[0] === 'object' &&
          'id' in transformedData[0] &&
          Array.isArray((transformedData[0] as any).data) &&
          ((transformedData[0] as any).data.length === 0 ||
            (typeof (transformedData[0] as any).data[0] === 'object' &&
              'x' in (transformedData[0] as any).data[0] &&
              'y' in (transformedData[0] as any).data[0]));

        if (isLineChart && looksLikeLineSeries) {
          const mergedTileDef = {
            ...tileDefinition,
            settings: {
              ...(tileDefinition?.settings ?? {}),
              ...(runtimeSettings ?? {}),
            },
          };

          const fields = ['id', 'data.x', 'data.y'];

          return {
            data: transformedData,
            keys: [],
            config: {},
            columns: [],
            rows: [],
            tileDef: mergedTileDef,
            fields,
            effectiveSettings: runtimeSettings,
          };
        }

        const fields = flattenFields(transformedData);
        const previewResult = getPreviewTileDataAndDefinition(
          chartId,
          tileDefinition,
          transformedData,
          fields,
          runtimeSettings
        );

        const mergedTileDef = {
          ...previewResult.tileDef,
          settings: {
            ...(previewResult.tileDef?.settings ?? {}),
            ...(runtimeSettings ?? {}),
          },
        };

        const keys = Array.isArray(previewResult.previewTileData?.config?.series)
          ? previewResult.previewTileData.config.series.map((s: any) => s.dataKey)
          : deriveSeriesKeysFromData(previewResult.previewTileData?.data || []);

        return {
          data: previewResult.previewTileData?.data || [],
          keys,
          config: previewResult.previewTileData?.config || {},
          columns: previewResult.previewTileData?.columns || [],
          rows: previewResult.previewTileData?.rows || [],
          tileDef: mergedTileDef,
          fields,
          effectiveSettings: runtimeSettings,
        };
      } catch (err: any) {
        return {
          __error: `Error processing tile data: ${err?.message || String(err)}`,
        } as any;
      }
    }, [
      suppressReduxInit,
      customData,
      queriesOverride,
      transformationsOverride,
      settingsOverride,
      fetchedQueryResults,
      tileDefinition,
      chartId,
      effectiveQueriesForFetch,
      effectiveTransformations,
      effectiveSettings,
    ]);

    useEffect(() => {
      const err = (processed as any)?.__error ?? null;
      setPipelineError(err);
      if (err && !hasCompletedInitialFetchRef.current) {
        hasCompletedInitialFetchRef.current = true;
        setInitialized(true);
      }
    }, [processed]);

    useEffect(() => {
      if (suppressReduxInit || !processed?.fields?.length || !hasTileBuilderEntry) return;

      const nextSig = stableStringify(processed.fields);
      if (lastFieldsSignatureRef.current === nextSig) return;
      lastFieldsSignatureRef.current = nextSig;

      dispatch(setFields({ chartId, fields: processed.fields }));
    }, [suppressReduxInit, processed?.fields, hasTileBuilderEntry, dispatch, chartId]);

    const lastSentErrorRef = useRef<string | null>(null);
    useEffect(() => {
      if (!setTileError) return;
      const next = pipelineError ?? null;
      if (lastSentErrorRef.current === next) return;
      lastSentErrorRef.current = next;
      setTileError(next);
    }, [pipelineError, setTileError]);

    const setError = useCallback(
      (error: string | null) => {
        if (setTileError) setTileError(error);
      },
      [setTileError]
    );

    const runtimeType = normalizeRuntimeType(processed?.tileDef?.chart_type);
    const effectiveChartWidth = Math.max(layoutWidth, MIN_RENDER_WIDTH);
    const barDirection = inferBarDirection(tileDefinition.chart_type, processed?.effectiveSettings);
    const chartData = { keys: processed?.keys || [], data: processed?.data || [] };

    const tileContentWrapperStyle: React.CSSProperties = {
      width: '100%',
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      flex: 1,
      minHeight: 0,
      minWidth: 0,
      overflow: 'visible',
      boxSizing: 'border-box',
      position: 'relative',
      ...(settingsOverride?.outlineTileContent ? { border: 'solid 1px blue' } : {}),
    };

    const chartRenderKey = useMemo(() => {
      return stableStringify({
        tileId: tileDefinition.id,
        chartType: tileDefinition.chart_type,
        runtimeType,
        fetchSignature,
        layoutWidth,
        layoutHeight,
        layoutVersion,
        displayContext,
      });
    }, [
      tileDefinition.id,
      tileDefinition.chart_type,
      runtimeType,
      fetchSignature,
      layoutWidth,
      layoutHeight,
      layoutVersion,
      displayContext,
    ]);

    const renderChart = () => {
      switch (runtimeType) {
        case 'bar_chart':
          return (
            <BarChartTile
              key={`bar-${chartRenderKey}`}
              tileDef={processed.tileDef}
              layoutHeight={Math.max(1, layoutHeight)}
              layoutWidth={effectiveChartWidth}
              data={chartData}
              direction={barDirection}
              showLegendOverride={processed.effectiveSettings?.show_legend}
              setError={setError}
              initialized={initialized && !isFetching}
              displayContext={displayContext}
            />
          );

        case 'donut_chart':
          return (
            <DonutChartTile
              key={`donut-${chartRenderKey}`}
              variant="donut"
              tileDef={processed.tileDef}
              data={processed.data}
              layoutHeight={layoutHeight}
              layoutWidth={effectiveChartWidth}
              showLegendOverride={processed.effectiveSettings?.show_legend}
              setError={setError}
              initialized={initialized && !isFetching}
              displayContext={displayContext}
            />
          );

        case 'pie_chart':
          return (
            <PieChartTile
              key={`pie-${chartRenderKey}`}
              tileDef={processed.tileDef}
              data={processed.data}
              layoutHeight={layoutHeight}
              layoutWidth={effectiveChartWidth}
              showLegendOverride={processed.effectiveSettings?.show_legend}
              setError={setError}
              initialized={initialized && !isFetching}
              displayContext={displayContext}
            />
          );

        case 'gauge_chart':
          return (
            <GaugeChartTile
              key={`gauge-${chartRenderKey}`}
              tileDef={processed.tileDef}
              data={processed.data}
              layoutHeight={layoutHeight}
              layoutWidth={effectiveChartWidth}
              setError={setError}
              initialized={initialized && !isFetching}
              displayContext={displayContext}
            />
          );

        case 'line_chart':
          return (
            <LineChartTile
              key={`line-${chartRenderKey}`}
              tileDef={processed.tileDef}
              data={processed.data}
              layoutHeight={layoutHeight}
              layoutWidth={effectiveChartWidth}
              setError={setError}
              initialized={initialized && !isFetching}
              layoutVersion={layoutVersion}
              isPreview={false}
              displayContext={displayContext}
            />
          );

        case 'metric_chart':
          return (
            <MetricChartTile
              key={`metric-${chartRenderKey}`}
              data={processed.data}
              setError={setError}
              initialized={initialized && !isFetching}
            />
          );

        case 'datagrid':
          return (
            <DataGridTile
              key={`datagrid-${chartRenderKey}`}
              columns={processed.columns}
              rows={processed.rows}
              setError={setError}
              initialized={initialized && !isFetching}
            />
          );

        default:
          return <div>Chart type not found: {runtimeType}</div>;
      }
    };

    if ((processed as any)?.__error) {
      return (
        <div ref={containerRef} style={tileContentWrapperStyle}>
          <div style={{ padding: 12 }}>{pipelineError || (processed as any).__error}</div>
        </div>
      );
    }

    return (
      <div ref={containerRef} style={tileContentWrapperStyle} className="relative">
        {pipelineError ? (
          <div style={{ padding: 12 }}>{pipelineError}</div>
        ) : !initialized ? (
          <div
            style={{
              width: '100%',
              height: '100%',
              minHeight: 120,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <DataLoading queries={effectiveQueriesForFetch} fallback="Loading..." />
          </div>
        ) : (
          <>
            <div
              style={{
                width: '100%',
                height: '100%',
                opacity: isFetching ? 0.35 : 1,
                transition: 'opacity 160ms ease',
                pointerEvents: isFetching ? 'none' : 'auto',
              }}
            >
              {processed ? renderChart() : <div style={{ padding: 12 }}>No data</div>}
            </div>

            {isFetching && (
              <div
                style={{
                  position: 'absolute',
                  inset: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  background: 'rgba(255,255,255,0.45)',
                  backdropFilter: 'blur(1px)',
                  zIndex: 2,
                  transition: 'opacity 160ms ease',
                }}
              >
                <DataLoading queries={effectiveQueriesForFetch} fallback="Loading..." />
              </div>
            )}
          </>
        )}
      </div>
    );
  }
);

DataVisualizationComponent.displayName = 'DataVisualizationComponent';
export default DataVisualizationComponent;